<!-- Fixed 12-month report for one location (not tied to URL date filters). -->
<template>
    <section class="fixed-report">
        <header class="fixed-report-header">
            <h2>12-month overview</h2>
            <p class="fixed-report-subtitle">
                Last 12 full calendar months through {{ periodLabel || "previous month" }}.
                Follows the location filter above, but not the date or experience filters.
            </p>
        </header>

        <template v-if="applicable">
            <p v-if="loading" class="fixed-report-status">Loading 12-month data…</p>
            <p v-else-if="error" class="fixed-report-status fixed-report-status--error">{{ error }}</p>

            <template v-else-if="ready">
            <dashboard-summary-cards :summary="summary" />

            <dashboard-panel title="Bookings per month" :subtitle="`${periodLabel} · incl. trend line (0 = no bar visible)`">
                <dashboard-change-badge :change="bookingsYearChange" :label="bookingsYearChangeLabel" />
                <dashboard-bar-chart-host
                    :labels="monthLabels"
                    :datasets="monthlyDatasets"
                    :extra-options="monthlyChartOptions"
                />
            </dashboard-panel>

            <section class="panel-grid">
                <dashboard-panel title="Games played" subtitle="Share of bookings by experience">
                    <dashboard-bar-chart-host
                        wrap-class="compact"
                        :labels="experienceLabels"
                        :datasets="experienceDatasets"
                    />
                </dashboard-panel>
                <dashboard-panel title="Popular weekdays" subtitle="Bookings by day of week">
                    <dashboard-bar-chart-host
                        wrap-class="compact"
                        :labels="weekdayLabels"
                        :datasets="weekdayDatasets"
                    />
                </dashboard-panel>
                <dashboard-panel title="Languages" subtitle="Bookings by language">
                    <dashboard-bar-chart-host
                        wrap-class="compact"
                        :labels="languageLabels"
                        :datasets="languageDatasets"
                    />
                </dashboard-panel>
            </section>
        </template>
        </template>
    </section>
</template>

<script setup>
import DashboardBarChartHost from "./DashboardBarChartHost.vue";
import DashboardChangeBadge from "./DashboardChangeBadge.vue";
import DashboardPanel from "./DashboardPanel.vue";
import DashboardSummaryCards from "./DashboardSummaryCards.vue";

const monthlyChartOptions = {
    plugins: {
        tooltip: {
            callbacks: {
                label(context) {
                    const value = context.parsed?.y ?? context.raw ?? 0;
                    return `${context.dataset.label}: ${value}`;
                },
            },
        },
    },
    scales: {
        x: {
            ticks: {
                // Always show all 12 months — autoSkip was hiding the last months on narrow panels.
                autoSkip: false,
                maxRotation: 60,
                minRotation: 45,
            },
        },
    },
};

defineProps({
    applicable: { type: Boolean, default: false },
    ready: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    error: { type: String, default: "" },
    periodLabel: { type: String, default: "" },
    bookingsYearChange: { type: Object, default: null },
    bookingsYearChangeLabel: { type: String, default: "" },
    summary: { type: Object, default: () => ({}) },
    monthLabels: { type: Array, default: () => [] },
    monthlyDatasets: { type: Array, default: () => [] },
    experienceLabels: { type: Array, default: () => [] },
    experienceDatasets: { type: Array, default: () => [] },
    weekdayLabels: { type: Array, default: () => [] },
    weekdayDatasets: { type: Array, default: () => [] },
    languageLabels: { type: Array, default: () => [] },
    languageDatasets: { type: Array, default: () => [] },
});
</script>

<style scoped lang="scss">
.fixed-report {
    margin-top: var(--s-5);
    padding-top: var(--s-5);
    border-top: 1px solid var(--line);
    display: flex;
    flex-direction: column;
    gap: var(--s-4);
}

.fixed-report-header {
    h2 {
        margin: 0 0 4px;
        font-family: var(--display-form);
        font-size: 18px;
        color: var(--text);
    }
}

.fixed-report-subtitle {
    margin: 0;
    color: var(--muted);
    font-size: 14px;
    max-width: 52rem;
}

.fixed-report-status {
    margin: 0.5rem 0 1rem;
    color: var(--muted);
    font-size: 14px;

    &--error {
        color: var(--rood);
    }
}

.panel-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--s-3);
    align-items: stretch;

    > :deep(.paneel) {
        min-width: 0;
    }
}

@media (min-width: 900px) {
    .panel-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (min-width: 1200px) {
    .panel-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}
</style>
